'use client';

import React, { useState } from 'react';
import Link from 'next/link';

type Tier = 'student' | 'undergraduate' | 'premium' | 'seniors';

interface Plan {
  tier: Tier;
  label: string;
  price: number;
  description: string;
  features: string[];
  gradient: string;
  badge?: string;
}

const PLANS: Plan[] = [
  {
    tier: 'student',
    label: 'Student',
    price: 4200,
    description: 'For students starting their growth journey',
    features: [
      'Rafeeq AI کے ساتھ وائس چیٹ',
      'روزانہ حوصلہ افزا ویڈیوز',
      'عادات ٹریکنگ اور اسٹریک',
      'نماز کے اوقات اور قبلہ',
      '9 زبانوں کی سپورٹ',
    ],
    gradient: 'from-emerald-500 to-teal-600',
  },
  {
    tier: 'undergraduate',
    label: 'Undergraduate',
    price: 5800,
    description: 'For undergraduates building their future',
    features: [
      'Student کی تمام سہولیات',
      'تعلیمی تحقیق موڈ',
      'گفتگو کی توسیع شدہ تاریخ',
      'ترجیحی AI جوابات',
      'ہفتہ وار ترقی کا تجزیہ',
    ],
    gradient: 'from-blue-500 to-indigo-600',
    badge: 'مقبول',
  },
  {
    tier: 'premium',
    label: 'Premium',
    price: 8500,
    description: 'For professionals & entrepreneurs',
    features: [
      'Undergraduate کی تمام سہولیات',
      'Career Launchpad تک رسائی',
      'جدید آواز انضمام',
      'لامحدود AI کوچنگ',
      'کاروباری اہداف ٹریکنگ',
    ],
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    tier: 'seniors',
    label: 'بزرگ',
    price: 7000,
    description: 'بزرگوں کے لیے خصوصی عبادت رہنمائی',
    features: [
      'ذاتی عبادت رہنمائی',
      'نجی علماء سوال و جواب (شیخ منتخب کریں)',
      'سوال ریکارڈ یا ٹائپ کریں',
      'جوابات صرف آپ کو نجی طور پر',
      'بزرگوں کے لیے دینی مواد',
      'نماز اور ذکر کی یاددہانی',
    ],
    gradient: 'from-purple-600 to-violet-700',
    badge: 'صرف بزرگوں کے لیے',
  },
];

type CheckoutStep = 'form' | 'waiting' | 'success' | 'failed';

interface PakistanCheckoutProps {
  plan: Plan;
  onSuccess: () => void;
  onCancel: () => void;
}

function PakistanCheckout({ plan, onSuccess, onCancel }: PakistanCheckoutProps) {
  const [phone, setPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'jazzcash' | 'easypaisa'>('jazzcash');
  const [step, setStep] = useState<CheckoutStep>('form');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('براہ کرم اپنا موبائل نمبر درج کریں۔');
      return;
    }

    const cleaned = phone.replace(/\s+/g, '').replace(/^0/, '92');
    if (!/^923[0-9]{9}$/.test(cleaned)) {
      setError('براہ کرم درست پاکستانی موبائل نمبر درج کریں (مثال: 0300 1234567)۔');
      return;
    }

    setLoading(true);
    setStep('waiting');

    try {
      const apiRoute = paymentMethod === 'jazzcash' ? '/api/payments/jazzcash' : '/api/payments/easypaisa';
      const response = await fetch(apiRoute, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleaned, amount: plan.price, tier: plan.tier }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'ادائیگی ناکام ہوئی۔ دوبارہ کوشش کریں۔');
        setStep('failed');
      } else {
        setStep('success');
        onSuccess();
      }
    } catch {
      setError('نیٹ ورک خرابی۔ اپنا کنکشن چیک کریں اور دوبارہ کوشش کریں۔');
      setStep('failed');
    } finally {
      setLoading(false);
    }
  };

  const paymentMethods = [
    { id: 'jazzcash' as const, label: 'JazzCash', emoji: '🔴', color: 'text-red-600 bg-red-50 border-red-200' },
    { id: 'easypaisa' as const, label: 'EasyPaisa', emoji: '🟢', color: 'text-green-600 bg-green-50 border-green-200' },
  ];

  return (
    <div className="w-full max-w-md mx-auto">
      <div className={`bg-gradient-to-r ${plan.gradient} rounded-2xl p-6 mb-6 text-white`}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-xl">📱</span>
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium">موبائل ادائیگی</p>
            <p className="text-white font-700 text-lg">{plan.label} Plan</p>
          </div>
        </div>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="text-3xl font-800">PKR {plan.price.toLocaleString()}</span>
          <span className="text-white/70 text-sm">/ماہ</span>
        </div>
      </div>

      {step === 'form' && (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-600 text-foreground mb-2">ادائیگی کا طریقہ</label>
            <div className="grid grid-cols-2 gap-2">
              {paymentMethods.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setPaymentMethod(m.id)}
                  className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border text-xs font-600 transition-all ${
                    paymentMethod === m.id ? m.color + ' ring-2 ring-offset-1 ring-current' : 'border-border bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <span className="text-xl">{m.emoji}</span>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-600 text-foreground mb-2">موبائل نمبر</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm font-medium">🇵🇰 +92</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0300 1234567"
                className="w-full pl-20 pr-4 py-3 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                maxLength={12}
                autoComplete="tel"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {paymentMethod === 'jazzcash' ? 'JazzCash' : 'EasyPaisa'} سے رجسٹرڈ نمبر
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <span className="text-red-500 text-sm mt-0.5">⚠️</span>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-600 text-foreground">ادائیگی کا طریقہ کار:</p>
            <div className="space-y-1.5">
              {[
                'اپنا موبائل نمبر درج کریں',
                'آپ کے فون پر ادائیگی کی درخواست آئے گی',
                'اپنا PIN درج کر کے ادائیگی منظور کریں',
                'سبسکرپشن فوری طور پر فعال ہو جائے گی',
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs font-700 text-primary bg-primary/10 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-xs text-muted-foreground" dir="rtl">{s}</p>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3.5 px-4 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60`}
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                پروسیسنگ…
              </>
            ) : (
              <>
                <span>📲</span>
                PKR {plan.price.toLocaleString()} ادا کریں
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← پلانز پر واپس جائیں
          </button>
        </form>
      )}

      {step === 'waiting' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">ادائیگی کا انتظار…</h3>
            <p className="text-sm text-muted-foreground">اپنے فون پر موبائل منی کی درخواست منظور کریں۔</p>
          </div>
        </div>
      )}

      {step === 'success' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">ادائیگی کامیاب! 🎉</h3>
            <p className="text-sm text-muted-foreground">آپ کی Rafeeq سبسکرپشن اب فعال ہے۔</p>
          </div>
          <Link
            href="/dashboard"
            className={`block w-full py-3 px-6 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center`}
          >
            Dashboard پر جائیں →
          </Link>
        </div>
      )}

      {step === 'failed' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">ادائیگی ناکام</h3>
            <p className="text-sm text-muted-foreground">ایک خرابی پیش آئی۔ براہ کرم دوبارہ کوشش کریں۔</p>
          </div>
          <button
            onClick={() => { setStep('form'); setError(''); }}
            className={`block w-full py-3 px-6 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center`}
          >
            دوبارہ کوشش کریں
          </button>
        </div>
      )}
    </div>
  );
}

export default function PakistanPricingPage() {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-800 text-foreground mb-2">Rafeeq Premium میں خوش آمدید! 🎉</h1>
            <p className="text-muted-foreground">آپ کی سبسکرپشن اب فعال ہے۔ اپنا سفر شروع کریں۔</p>
          </div>
          <a
            href="/dashboard"
            className="block w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center"
          >
            Dashboard پر جائیں →
          </a>
        </div>
      </div>
    );
  }

  if (selectedPlan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <button
            onClick={() => setSelectedPlan(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            پلانز پر واپس جائیں
          </button>
          <PakistanCheckout
            plan={selectedPlan}
            onSuccess={() => { setPaymentSuccess(true); setSelectedPlan(null); }}
            onCancel={() => setSelectedPlan(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-600 mb-4">
            <span>🇵🇰</span>
            <span>JazzCash · EasyPaisa · پاکستان</span>
          </div>
          <h1 className="text-3xl font-800 text-foreground mb-3">اپنا Rafeeq پلان منتخب کریں</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            AI کوچنگ، وائس چیٹ، اور روزانہ ترقی کے ٹولز تک رسائی حاصل کریں۔ JazzCash یا EasyPaisa سے محفوظ ادائیگی کریں۔
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {PLANS.map((plan) => (
            <div
              key={plan.tier}
              className={`relative bg-card border rounded-2xl overflow-hidden transition-all hover:shadow-lg ${
                plan.badge ? 'border-blue-300 shadow-blue-100 shadow-md' : 'border-border'
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-700 px-3 py-1 rounded-bl-xl">
                  {plan.badge}
                </div>
              )}
              <div className={`bg-gradient-to-r ${plan.gradient} p-6 text-white`}>
                <h3 className="text-lg font-700 mb-1">{plan.label}</h3>
                <p className="text-white/70 text-xs mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-800">PKR {plan.price.toLocaleString()}</span>
                  <span className="text-white/70 text-sm">/ماہ</span>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-foreground" dir="rtl">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full py-3 px-4 bg-gradient-to-r ${plan.gradient} text-white font-600 text-sm rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  <span>📲</span>
                  یہ پلان منتخب کریں
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { icon: '🔒', title: 'محفوظ ادائیگی', desc: 'انکرپٹڈ پیمنٹ گیٹ وے' },
              { icon: '⚡', title: 'فوری ایکٹیویشن', desc: 'سبسکرپشن سیکنڈوں میں فعال' },
              { icon: '📱', title: 'کارڈ کی ضرورت نہیں', desc: 'موبائل والیٹ سے براہ راست ادائیگی' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <span className="text-2xl">{item.icon}</span>
                <p className="text-sm font-600 text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          قیمتیں پاکستانی روپے (PKR) میں۔ سبسکرپشن ماہانہ تجدید ہوتی ہے۔ کسی بھی وقت منسوخ کریں۔
        </p>
      </div>
    </div>
  );
}
